import useCampaigns from '../useCampaigns';
import { isNumber } from 'utility/utility';
import moment from 'moment';
import uniq from 'lodash/uniq';
import { useTiktokAudios } from '@round/platforms/tiktok';
import { useCreatorbaseTimeSeries } from '@round/creatorbase-helpers/api-hooks';
import useNonNullContext from 'Hooks/useNonNullContext';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import { buildTiktokMusicUrl } from 'helpers';
import { CampaignTableRow } from '../CampaignsTable/ReportCampaignsTable';

export function useReportTiktokCampaigns() {
    const { currencies } = useNonNullContext(OptionsContext);
    const {
        campaignsData,
        projectsData,
        brandsData,
        teamsData,
        campaignStatsData,
        isTeamsLoading,
        isProjectsLoading,
        getIsBrandLoading,
        getIsCampaignStatsLoading,
    } = useCampaigns('tiktok');

    const audioIds = uniq(
        campaignsData.data?.results.map((campaign) => campaign.tiktok_details?.audio_id).filter(isNumber) ?? []
    );

    const { data: tiktokAudiosData } = useTiktokAudios(
        { id: audioIds.join(','), page_size: audioIds.length },
        {
            enabled: audioIds.length > 0,
            select: (data) => new Map(data.results.map((audio) => [audio.id, audio])),
        }
    );

    const {
        data: audioTimeSeriesData,
        isLoading: isAudioTimeSeriesLoading,
        isError: isAudioTimeSeriesError,
    } = useCreatorbaseTimeSeries(
        {
            type: 'tiktok_audio',
            ids: audioIds,
            fields: ['posts_daily_change'],
            start_date: moment().subtract(14, 'days').format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
        },
        { enabled: audioIds.length > 0 }
    );

    const rows: CampaignTableRow[] =
        campaignsData.data?.results?.map((campaign) => {
            const project = projectsData.get(campaign.project_id) ?? null;
            const brand = (project && brandsData[project.brand_id]?.data) || null;
            const team = project ? teamsData?.get(project.team_id) ?? null : null;
            const currency = currencies.find((c) => c.id === campaign.currency_id);
            const audioTimeSeries = campaign.tiktok_details?.audio_id
                ? audioTimeSeriesData?.[campaign.tiktok_details.audio_id] ?? null
                : null;
            const audio = campaign.tiktok_details?.audio_id
                ? tiktokAudiosData?.get(campaign.tiktok_details.audio_id) ?? null
                : null;
            const audioUrl =
                audio?.tiktok_id && audio.title ? buildTiktokMusicUrl(audio.title, audio.tiktok_id) : undefined;
            return {
                ...campaign,
                stats: campaignStatsData[campaign.id]?.data ?? null,
                project,
                brand,
                team,
                currency,
                audioTimeSeries,
                audioUrl,
            };
        }) ?? [];

    return {
        rows,
        isLoading: campaignsData.isLoading,
        isTeamsLoading,
        isProjectsLoading,
        isAudioTimeSeriesLoading,
        isAudioTimeSeriesError,
        getIsBrandLoading,
        getIsCampaignStatsLoading,
    };
}
